import React, { useState, useEffect } from 'react';
import { TrendingUp, BarChart2, Calendar, PieChart, FileText, Shield, Zap, Check, ArrowRight, Gift, Twitter, Instagram, Facebook, Newspaper } from 'lucide-react';
import axios from 'axios';
import CryptoTicker from './CryptoTicker';

const API = process.env.REACT_APP_BACKEND_URL;

const LandingPage = ({ onGetStarted, onLogin, onPromoCode, onAdmin, onNavigate }) => {
  const features = [
    {
      icon: <BarChart2 className="w-6 h-6" />,
      title: "Análisis Completo de PNL",
      description: "Visualiza tu rendimiento diario, semanal y mensual con gráficos interactivos"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Calendario de Trading",
      description: "Vista calendario con colores que muestran ganancias y pérdidas por día"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Tendencia de Activos",
      description: "Sigue la evolución de tu capital con gráficos de tendencia detallados"
    },
    {
      icon: <PieChart className="w-6 h-6" />,
      title: "Comparativas Avanzadas",
      description: "Compara tu rendimiento mensual, trimestral y contra Bitcoin"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Exportar a PDF",
      description: "Genera informes profesionales en PDF para tu análisis"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Multi-Broker",
      description: "Compatible con MEXC, Quantfury y más plataformas de trading"
    }
  ];

  const plans = [
    {
      name: "Plan Mensual",
      price: "2.99€",
      period: "/mes",
      description: "Ideal para probar",
      features: ["Operaciones ilimitadas", "Todos los gráficos", "Exportar a PDF"]
    },
    {
      name: "Plan Anual",
      price: "26.90€",
      period: "/año",
      savings: "Ahorra 25%",
      description: "La opción más equilibrada",
      features: ["Operaciones ilimitadas", "Todos los gráficos", "Exportar a PDF"]
    },
    {
      name: "Acceso de por Vida",
      price: "59€",
      period: "pago único",
      popular: true,
      description: "Inversión a largo plazo",
      limitedOffer: true,
      features: ["Operaciones ilimitadas", "Todos los gráficos", "Exportar a PDF", "Actualizaciones de por vida"]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0b]/80 backdrop-blur-lg border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-center h-20 gap-8">
            <div className="flex-shrink-0">
              <img src="/logo.png" alt="TraceMetrics" className="h-14 w-auto" />
            </div>
            <div className="flex items-center gap-6">
              <button
                onClick={() => onNavigate && onNavigate('help')}
                className="text-gray-300 hover:text-white transition-colors text-sm hidden sm:block"
                data-testid="nav-help-btn"
              >
                Ayuda
              </button>
              <button
                onClick={() => onNavigate && onNavigate('terminal-landing')}
                className="flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 transition-colors text-sm"
                data-testid="nav-terminal-btn"
              >
                <Newspaper className="w-4 h-4" />
                Terminal
              </button>
              <button
                onClick={() => onNavigate && onNavigate('polymarket')}
                className="flex items-center gap-1.5 text-teal-400 hover:text-teal-300 transition-colors text-sm"
                data-testid="nav-polymarket-btn"
              >
                <PieChart className="w-4 h-4" />
                Polymarket
              </button>
              <button
                onClick={onPromoCode}
                className="flex items-center gap-1.5 text-purple-400 hover:text-purple-300 transition-colors text-sm"
                data-testid="nav-promo-btn"
              >
                <Gift className="w-4 h-4" />
                ¿Tienes un código?
              </button>
              <button
                onClick={onLogin}
                className="text-gray-300 hover:text-white transition-colors text-sm"
                data-testid="nav-login-btn"
              >
                Iniciar Sesión
              </button>
              <button
                onClick={onGetStarted}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg text-sm font-medium hover:from-cyan-400 hover:to-teal-400 transition-all"
                data-testid="nav-register-btn"
              >
                Comenzar Gratis
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Crypto Ticker Banner */}
      <CryptoTicker />

      {/* Hero Section */}
      <section className="pt-40 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/10 to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm mb-8">
            <Zap className="w-4 h-4" />
            Análisis de trading profesional
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Analiza tus operaciones<br />
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">
              como un profesional
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10">
            Importa tus datos de trading de MEXC, Quantfury y más. Obtén insights detallados sobre tu rendimiento con gráficos interactivos y métricas avanzadas.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onGetStarted}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl font-semibold text-lg hover:from-cyan-400 hover:to-teal-400 transition-all flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/25"
              data-testid="hero-cta-btn"
            >
              Prueba Gratis (20 operaciones)
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={onLogin}
              className="w-full sm:w-auto px-8 py-4 border border-gray-700 rounded-xl font-semibold text-lg hover:bg-gray-800 transition-all"
            >
              Ya tengo cuenta
            </button>
          </div>
        </div>
      </section>

      {/* Feature Showcase Sections (alternating text + screenshot) */}
      <section className="py-24 px-4 bg-[#0a0a0b]">
        <div className="max-w-6xl mx-auto space-y-28">
          {/* Showcase 1: Dashboard Overview */}
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-5/12 space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-xs">
                <BarChart2 className="w-3.5 h-3.5" /> Dashboard
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold leading-tight">
                Tu rendimiento,<br /><span className="text-cyan-400">de un vistazo</span>
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Panel principal con PNL total, volumen, win rate, operaciones ganadoras y perdedoras, capital inicial y final. Toda la información que necesitas para tomar mejores decisiones.
              </p>
            </div>
            <div className="lg:w-7/12">
              <div className="rounded-xl overflow-hidden border border-gray-800 shadow-2xl shadow-cyan-500/5">
                <img src="/screenshots/feature-dashboard.png" alt="Dashboard de TraceMetrics" className="w-full" loading="lazy" />
              </div>
            </div>
          </div>

          {/* Showcase 2: Calendar & Stats (reversed) */}
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            <div className="lg:w-5/12 space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/10 border border-teal-500/20 rounded-full text-teal-400 text-xs">
                <Calendar className="w-3.5 h-3.5" /> Calendario
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold leading-tight">
                Calendario de<br /><span className="text-teal-400">PNL diario</span>
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Visualiza cada día de trading con colores: verde para ganancias, rojo para pérdidas. Identifica patrones y mejora tu consistencia con una vista mensual intuitiva.
              </p>
            </div>
            <div className="lg:w-7/12">
              <div className="rounded-xl overflow-hidden border border-gray-800 shadow-2xl shadow-teal-500/5">
                <img src="/screenshots/feature-calendar.png" alt="Calendario PNL de TraceMetrics" className="w-full" loading="lazy" />
              </div>
            </div>
          </div>

          {/* Showcase 3: Charts & Comparison */}
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-5/12 space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-xs">
                <TrendingUp className="w-3.5 h-3.5" /> Comparativas
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold leading-tight">
                Compara tu rendimiento<br /><span className="text-purple-400">contra Bitcoin</span>
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Gráficos de comparación mensual, trimestral y evolución de capital. Comprueba si tu estrategia supera al mercado con métricas objetivas.
              </p>
            </div>
            <div className="lg:w-7/12">
              <div className="rounded-xl overflow-hidden border border-gray-800 shadow-2xl shadow-purple-500/5">
                <img src="/screenshots/feature-bitcoin.png" alt="Comparativas de TraceMetrics" className="w-full" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-[#0d0d0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Todo lo que necesitas para{' '}
              <span className="text-cyan-400">mejorar tu trading</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Herramientas potentes para analizar, visualizar y optimizar tus operaciones
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="p-6 bg-[#0a0a0b] border border-gray-800 rounded-xl hover:border-cyan-500/50 transition-all group"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-lg flex items-center justify-center text-cyan-400 mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Planes simples,{' '}
              <span className="text-cyan-400">precios transparentes</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Comienza gratis con 20 operaciones. Actualiza cuando necesites más.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`p-6 rounded-xl border ${
                  plan.popular
                    ? 'bg-gradient-to-b from-cyan-500/10 to-transparent border-cyan-500 relative'
                    : 'bg-[#0d0d0f] border-gray-800'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full text-xs font-semibold">
                    MÁS POPULAR
                  </div>
                )}
                {plan.savings && (
                  <div className="inline-block px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium mb-2">
                    {plan.savings}
                  </div>
                )}
                <h3 className="text-xl font-semibold mb-1">{plan.name}</h3>
                {plan.description && (
                  <p className="text-gray-500 text-sm mb-4">{plan.description}</p>
                )}
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  {plan.limitedOffer && <span className="text-cyan-400 text-xl">*</span>}
                  <span className="text-gray-400 ml-1">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-5 h-5 text-cyan-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onGetStarted}
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    plan.popular
                      ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400'
                      : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  Comenzar
                </button>
              </div>
            ))}
          </div>
          
          {/* Limited offer note */}
          <p className="text-center text-gray-500 text-sm mt-6">
            * Oferta por tiempo limitado
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-8 sm:p-12 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border border-cyan-500/20 rounded-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              ¿Listo para mejorar tu trading?
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Únete a traders que ya analizan sus operaciones con TraceMetrics
            </p>
            <button
              onClick={onGetStarted}
              className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl font-semibold text-lg hover:from-cyan-400 hover:to-teal-400 transition-all inline-flex items-center gap-2"
              data-testid="final-cta-btn"
            >
              Comenzar Gratis
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 border-t border-gray-800">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-6">
          <img src="/logo.png" alt="TraceMetrics" className="h-14 w-auto" />
          <div className="flex items-center gap-4 text-sm flex-wrap justify-center">
            <button onClick={() => onNavigate && onNavigate('terms')} className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-terms-link">Términos y Condiciones</button>
            <span className="text-gray-700">|</span>
            <button onClick={() => onNavigate && onNavigate('privacy')} className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-privacy-link">Política de Privacidad</button>
            <span className="text-gray-700">|</span>
            <button onClick={() => onNavigate && onNavigate('contact')} className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-contact-link">Contacto</button>
            <span className="text-gray-700">|</span>
            <button onClick={() => onNavigate && onNavigate('help')} className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-help-link">Ayuda</button>
          </div>
          <div className="flex items-center gap-6">
            <a href="https://x.com/tracemetric2026" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" data-testid="social-twitter" title="X (Twitter)">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="#" className="text-gray-600 cursor-default" data-testid="social-instagram" title="Instagram (Próximamente)">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="#" className="text-gray-600 cursor-default" data-testid="social-facebook" title="Facebook (Próximamente)">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="#" className="text-gray-600 cursor-default" data-testid="social-tiktok" title="TikTok (Próximamente)">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.51a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.43v-7.15a8.16 8.16 0 005.58 2.2V11.2a4.85 4.85 0 01-2.96-1.04V6.69h2.96z"/></svg>
            </a>
          </div>
          <p className="text-gray-600 text-xs">© {new Date().getFullYear()} TraceMetrics</p>
          <button onClick={onAdmin} className="text-gray-700 hover:text-gray-500 text-xs transition-colors">Admin</button>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
